<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 scale-95 translate-y-2"
    enter-to-class="opacity-100 scale-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 scale-100 translate-y-0"
    leave-to-class="opacity-0 scale-95 -translate-y-2"
    @enter="onEnter"
    @leave="onLeave"
  >
    <div
      v-if="visible"
      ref="alertElement"
      :class="alertClasses"
      role="alert"
      :aria-live="ariaLive"
      :aria-atomic="true"
      :aria-label="accessibleLabel"
      tabindex="0"
    >
      <!-- Icon -->
      <div v-if="showIcon" class="flex-shrink-0">
        <component
          :is="alertIcon"
          :class="iconClasses"
          aria-hidden="true"
        />
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <!-- Title with enhanced semantics -->
        <h4
          v-if="title"
          :id="`${alertId}-title`"
          :class="titleClasses"
          :aria-level="titleLevel"
        >
          {{ title }}
        </h4>

        <!-- Message/Content with enhanced accessibility -->
        <div
          :id="`${alertId}-content`"
          :class="contentClasses"
          :aria-describedby="title ? `${alertId}-title` : undefined"
        >
          <slot v-if="$slots.default" />
          <p v-else-if="message">{{ message }}</p>
        </div>

        <!-- Actions with enhanced keyboard navigation -->
        <div
          v-if="$slots.actions"
          class="mt-3 flex items-center space-x-3"
          role="group"
          :aria-label="actionsAriaLabel"
        >
          <slot name="actions" />
        </div>
      </div>

      <!-- Close button with enhanced accessibility -->
      <button
        v-if="dismissible"
        ref="closeButton"
        type="button"
        :class="closeButtonClasses"
        :aria-label="closeAriaLabel"
        :aria-describedby="title ? undefined : `${alertId}-content`"
        @click="handleDismiss"
        @keydown.enter="handleDismiss"
        @keydown.space.prevent="handleDismiss"
        @keydown.escape="handleDismiss"
      >
        <svg
          class="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  </Transition>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'

// Props definition
const props = defineProps({
  // Alert variant/type
  variant: {
    type: String,
    default: 'info',
    validator: (value) => ['success', 'info', 'warning', 'error'].includes(value)
  },

  // Accessibility enhancements
  titleLevel: {
    type: [String, Number],
    default: 4,
    validator: (value) => [1, 2, 3, 4, 5, 6, '1', '2', '3', '4', '5', '6'].includes(value)
  },
  accessibleLabel: {
    type: String,
    default: null
  },
  actionsAriaLabel: {
    type: String,
    default: 'Alert actions'
  },
  
  // Alert size
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  
  // Content
  title: {
    type: String,
    default: null
  },
  message: {
    type: String,
    default: null
  },
  
  // Appearance
  icon: {
    type: [Boolean, String, Object],
    default: true
  },
  rounded: {
    type: Boolean,
    default: true
  },
  bordered: {
    type: Boolean,
    default: false
  },
  
  // Behavior
  dismissible: {
    type: Boolean,
    default: false
  },
  autoClose: {
    type: Boolean,
    default: false
  },
  autoCloseDelay: {
    type: Number,
    default: 5000 // 5 seconds
  },
  
  // Accessibility
  closeAriaLabel: {
    type: String,
    default: 'Close alert'
  },
  
  // Custom classes
  alertClass: {
    type: [String, Array, Object],
    default: null
  }
})

// Events
const emit = defineEmits(['dismiss', 'close', 'show', 'hide'])

// Template refs
const alertElement = ref(null)
const closeButton = ref(null)

// Reactive state
const visible = ref(true)
let autoCloseTimer = null

// Unique alert ID for accessibility
const alertId = ref(`alert-${Math.random().toString(36).substr(2, 9)}`)

// Computed properties
const showIcon = computed(() => {
  return props.icon !== false
})

// Enhanced aria-live based on variant and importance
const ariaLive = computed(() => {
  if (props.variant === 'error') return 'assertive'
  if (props.variant === 'warning') return 'assertive'
  return 'polite'
})

const alertIcon = computed(() => {
  // Use custom icon if provided
  if (typeof props.icon === 'string' || typeof props.icon === 'object') {
    return props.icon
  }
  
  // Default icons based on variant
  switch (props.variant) {
    case 'success':
      return 'CheckCircleIcon'
    case 'warning':
      return 'ExclamationTriangleIcon'
    case 'error':
      return 'XCircleIcon'
    default: // info
      return 'InformationCircleIcon'
  }
})

const alertClasses = computed(() => {
  const classes = [
    // Base styles
    'flex items-start space-x-3 p-4 transition-all duration-200',
    
    // Size variants
    {
      'p-3 text-sm': props.size === 'small',
      'p-4 text-base': props.size === 'medium',
      'p-5 text-lg': props.size === 'large'
    },
    
    // Rounded corners
    {
      'rounded-lg': props.rounded,
      'rounded-none': !props.rounded
    }
  ]

  // Enhanced variant-specific styling with accessibility
  switch (props.variant) {
    case 'success':
      if (props.bordered) {
        classes.push('bg-success-50 border border-success-200 shadow-sm shadow-success-100/50')
      } else {
        classes.push('bg-success-50 shadow-sm shadow-success-100/50')
      }
      break
    case 'warning':
      if (props.bordered) {
        classes.push('bg-warning-50 border border-warning-200 shadow-sm shadow-warning-100/50')
      } else {
        classes.push('bg-warning-50 shadow-sm shadow-warning-100/50')
      }
      break
    case 'error':
      if (props.bordered) {
        classes.push('bg-error-50 border border-error-200 shadow-sm shadow-error-100/50')
      } else {
        classes.push('bg-error-50 shadow-sm shadow-error-100/50')
      }
      break
    default: // info
      if (props.bordered) {
        classes.push('bg-primary-50 border border-primary-200 shadow-sm shadow-primary-100/50')
      } else {
        classes.push('bg-primary-50 shadow-sm shadow-primary-100/50')
      }
  }

  // Custom classes
  if (props.alertClass) {
    classes.push(props.alertClass)
  }

  return classes
})

const iconClasses = computed(() => {
  const classes = ['h-5 w-5']
  
  // Size adjustments
  if (props.size === 'small') {
    classes[0] = 'h-4 w-4'
  } else if (props.size === 'large') {
    classes[0] = 'h-6 w-6'
  }
  
  // Variant-specific colors
  switch (props.variant) {
    case 'success':
      classes.push('text-green-500')
      break
    case 'warning':
      classes.push('text-yellow-500')
      break
    case 'error':
      classes.push('text-red-500')
      break
    default: // info
      classes.push('text-primary-500')
  }
  
  return classes
})

const titleClasses = computed(() => {
  const classes = ['font-semibold mb-1']
  
  // Size variants
  if (props.size === 'small') {
    classes.push('text-sm')
  } else if (props.size === 'large') {
    classes.push('text-lg')
  } else {
    classes.push('text-base')
  }
  
  // Variant-specific colors
  switch (props.variant) {
    case 'success':
      classes.push('text-green-800')
      break
    case 'warning':
      classes.push('text-yellow-800')
      break
    case 'error':
      classes.push('text-red-800')
      break
    default: // info
      classes.push('text-primary-800')
  }
  
  return classes
})

const contentClasses = computed(() => {
  const classes = []
  
  // Size variants
  if (props.size === 'small') {
    classes.push('text-xs')
  } else if (props.size === 'large') {
    classes.push('text-base')
  } else {
    classes.push('text-sm')
  }
  
  // Variant-specific colors
  switch (props.variant) {
    case 'success':
      classes.push('text-green-700')
      break
    case 'warning':
      classes.push('text-yellow-700')
      break
    case 'error':
      classes.push('text-red-700')
      break
    default: // info
      classes.push('text-primary-700')
  }
  
  return classes
})

const closeButtonClasses = computed(() => {
  const classes = [
    'flex-shrink-0 rounded-lg p-1.5 transition-all duration-200 ease-out',
    'focus:outline-none focus:ring-3 focus:ring-offset-1',
    'hover:scale-105 active:scale-95'
  ]
  
  // Enhanced variant-specific hover and focus colors
  switch (props.variant) {
    case 'success':
      classes.push(
        'text-success-500 hover:bg-success-100 focus:bg-success-100 focus:ring-success-500/30'
      )
      break
    case 'warning':
      classes.push(
        'text-warning-500 hover:bg-warning-100 focus:bg-warning-100 focus:ring-warning-500/30'
      )
      break
    case 'error':
      classes.push(
        'text-error-500 hover:bg-error-100 focus:bg-error-100 focus:ring-error-500/30'
      )
      break
    default: // info
      classes.push(
        'text-primary-500 hover:bg-primary-100 focus:bg-primary-100 focus:ring-primary-500/30'
      )
  }
  
  return classes
})

// Methods with enhanced accessibility
const handleDismiss = () => {
  visible.value = false
  emit('dismiss')
  emit('close') // Backward compatibility
  emit('hide')

  // Return focus to previously focused element if available
  restoreFocus()
}

const show = () => {
  visible.value = true
  emit('show')
  // Focus management for screen readers
  nextTick(() => {
    if (alertElement.value && (props.variant === 'error' || props.variant === 'warning')) {
      alertElement.value.focus()
    }
  })
}

const hide = () => {
  visible.value = false
  emit('hide')
  restoreFocus()
}

// Focus management
let previouslyFocusedElement = null

const storeFocus = () => {
  previouslyFocusedElement = document.activeElement
}

const restoreFocus = () => {
  if (previouslyFocusedElement && typeof previouslyFocusedElement.focus === 'function') {
    previouslyFocusedElement.focus()
    previouslyFocusedElement = null
  }
}

// Animation callbacks
const onEnter = () => {
  storeFocus()
}

const onLeave = () => {
  restoreFocus()
}

const startAutoCloseTimer = () => {
  if (props.autoClose && props.autoCloseDelay > 0) {
    autoCloseTimer = setTimeout(() => {
      handleDismiss()
    }, props.autoCloseDelay)
  }
}

const clearAutoCloseTimer = () => {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
    autoCloseTimer = null
  }
}

// Lifecycle
onMounted(() => {
  startAutoCloseTimer()
})

onUnmounted(() => {
  clearAutoCloseTimer()
})

// Expose enhanced methods for parent components
defineExpose({
  dismiss: handleDismiss,
  show,
  hide,
  focus: () => {
    if (alertElement.value) {
      alertElement.value.focus()
    }
  },
  focusCloseButton: () => {
    if (closeButton.value) {
      closeButton.value.focus()
    }
  }
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Enhanced smooth transitions for professional feel */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Transform utilities with GPU acceleration */
.scale-95 {
  --tw-scale-x: 0.95;
  --tw-scale-y: 0.95;
  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
  will-change: transform;
}

.translate-y-2 {
  --tw-translate-y: 0.5rem;
  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
  will-change: transform;
}

.-translate-y-2 {
  --tw-translate-y: -0.5rem;
  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
  will-change: transform;
}

/* Enhanced focus ring styling for WCAG compliance */
.focus\:ring-3:focus {
  --tw-ring-width: 3px;
  --tw-ring-opacity: 0.3;
}

.focus\:ring-offset-1:focus {
  --tw-ring-offset-width: 1px;
}

/* Alert container enhancements */
[role="alert"] {
  /* Enhanced focus styling for screen readers */
  outline: none;
}

[role="alert"]:focus {
  box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.3);
}

/* Button micro-interactions */
button {
  will-change: transform;
}

.hover\:scale-105:hover {
  --tw-scale-x: 1.05;
  --tw-scale-y: 1.05;
  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) scale(var(--tw-scale-x), var(--tw-scale-y));
}

.active\:scale-95:active {
  --tw-scale-x: 0.95;
  --tw-scale-y: 0.95;
  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) scale(var(--tw-scale-x), var(--tw-scale-y));
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  [role="alert"] {
    border-width: 2px !important;
    font-weight: 600;
  }

  [role="alert"]:focus {
    box-shadow: 0 0 0 4px #000000;
  }

  button {
    border: 2px solid currentColor !important;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .transition-all {
    transition: none !important;
  }

  .scale-95,
  .translate-y-2,
  .-translate-y-2,
  .hover\:scale-105:hover,
  .active\:scale-95:active {
    transform: none !important;
    will-change: auto !important;
  }

  button {
    will-change: auto !important;
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  button {
    min-height: 44px;
    min-width: 44px;
    padding: 0.75rem;
  }

  .hover\:scale-105:hover {
    transform: none; /* Disable hover effects on touch */
  }
}

/* Print styles */
@media print {
  .transition-all {
    transition: none !important;
  }

  [role="alert"] {
    background: white !important;
    color: black !important;
    border: 2px solid black !important;
    box-shadow: none !important;
  }

  button {
    display: none !important; /* Hide interactive elements in print */
  }
}
</style>